// Source-of-truth for the `fg-color-*` foreground color utilities — the property-named
// successor to `text-color-*` (see RFC: RCIcon → utility component). Icons and text share
// this one family (both set `color`; icons via `fill: currentColor`). Each value maps
// losslessly to `.fg-color-{value}` → `var(--rc-color-{value})`.
//
// Split by contrast tier so consumers can gate what they expose:
//   - text-*   (4.5:1) body-text-safe — the full RCText-pickable set
//   - accent-* (3:1)   graphical / large-text only — icons + large text add these
//
// Kept in lock-step with the `fg-color` generator in classes-utility/colors.scss; the
// drift-guard test asserts every value maps to an emitted class at every breakpoint.
// Rebuild all.css after editing the SCSS (npm run build-styles).

// Contrast tier: normal body text (WCAG 4.5:1).
export const FG_TEXT_COLORS = [
  'text-primary',
  'text-secondary',
  'text-tertiary',
  'text-discount',
  'text-accent',
  'text-success',
  'text-urgent',
  'text-info',
  'text-loud',
  'text-primary-inverted',
  'text-primary-on-dark',
  'text-primary-on-light',
  'text-primary-disabled',
] as const;

// Contrast tier: graphical objects / large text (WCAG 3:1). Valid for icons and large
// text, not body text — so RCText excludes these while RCIcon includes them.
export const FG_ACCENT_COLORS = [
  'accent-info',
  'accent-loud',
  'accent-neutral',
  'accent-notice',
  'accent-success',
  'accent-urgent',
] as const;

// Surface-tracking values: inside a themed ancestor they take the surface's family color
// (text-themed 4.5:1, accent-themed 3:1); elsewhere they inherit. Var-backed (they read
// the themed-surface `--_theme-*` vars) rather than a fixed `--rc-color-*` token, so they
// live apart from the two fixed tiers. `text-themed` belongs to the text tier, and
// `accent-themed` to the accent tier, for prop-union gating.
export const FG_THEMED_COLORS = ['text-themed', 'accent-themed'] as const;

export const FG_COLORS = [...FG_TEXT_COLORS, ...FG_ACCENT_COLORS, ...FG_THEMED_COLORS] as const;

export type FgTextColor = typeof FG_TEXT_COLORS[number];
export type FgAccentColor = typeof FG_ACCENT_COLORS[number];
export type FgThemedColor = typeof FG_THEMED_COLORS[number];
export type FgColor = typeof FG_COLORS[number];

// A foreground-color value → its utility class at one breakpoint (`prefix` is `''` for
// the base and `tablet-` / `mobile-` otherwise).
export function fgColorClass(color: FgColor, prefix = ''): string {
  return `${prefix}fg-color-${color}`;
}
